/**
 * pickAirCoord — T-1.4.1 (M1 收尾).
 *
 * Convert a screen-space pointer position into a snapped voxel coord
 * (or null) for **floating / overhang** voxel placement. Unlike
 * pickDropCoord (T-1.3.3), this picker does not consult the voxels
 * Map or the scene; it walks a fixed set of horizontal planes and
 * returns the lowest-y hit snapped to the integer grid.
 *
 * MVP scope (PRD FR-103):
 *   - y in {0, 1, 2, 3} only. y > 3 is intentionally out of MVP.
 *   - The four planes are the ground (y=0) plus three "air" layers.
 *   - x and z snap via `snapToGrid`; y is taken from the plane index
 *     (so a hit on the y=2 plane always produces y=2).
 *   - The lowest-y hit wins (closest to the ground), so the default
 *     placement behaviour is "land on whatever is below".
 *   - When no plane is hit (ray parallel / pointing away), return
 *     `null`. The caller (MainCanvas) treats null as "no placement".
 *
 * Why a pure function (Constitution §2.2 + §2.3 #3):
 *   - **No store read / write** — voxels Map is the single data source
 *     for *placed* voxels. pickAirCoord decides geometry only; placing
 *     the voxel is the PlaceVoxel command's job (T-1.3.5).
 *   - **No MainScene ref** — MainCanvas owns the scene and injects
 *     the raycaster + camera. This module never imports MainScene.
 *   - **No second 3D pipeline** — we only use Three's pure-math
 *     primitives (Plane, Vector2, Vector3, Raycaster).
 *   - **No InstancedMesh** — M3 will paint voxels. This module does
 *     not render anything; it only decides a coord.
 *   - **Scene-free signature** — pickAirCoord(raycaster, camera,
 *     ndcX, ndcY). It does NOT take the scene or voxelMeshes because
 *     it doesn't walk either; pickDropCoord does that work, and
 *     keeping the two pickers narrow is what lets us swap them in
 *     MainCanvas without churning the call site.
 *
 * Coordinate mapping:
 *   - `VoxelCoord` is the *min corner* of the voxel cell (SPEC §2.1).
 *   - `hit.point` from a raycast is the world-space hit position.
 *     x and z go through snapToGrid; y is taken from the plane that
 *     produced the hit (no rounding — the planes are integer-aligned).
 *   - `Math.max(0, …)` clamps y to non-negative; negative y is
 *     mathematically unreachable here (planes live at y ≥ 0) but
 *     we clamp defensively in case the caller injects a custom
 *     plane list in a future expansion.
 */

import { Plane, Raycaster, Vector2, Vector3, type Camera } from 'three';
import type { VoxelCoord } from '@/state/types';
import { snapToGrid } from '@/utils/coords';

/** MVP upper bound on the floating layer. y=4 and above are out of
 *  scope for T-1.4.1 and would require a continuous-y picker (later). */
const MAX_AIR_Y = 3;

/**
 * The four horizontal planes pickAirCoord walks, in order y=0..y=3.
 * Each plane is `normal = (0,1,0)` and `constant = -y`, so
 * `ray.intersectPlane(plane, …)` writes the world-space hit point
 * into the target. THREE.Plane is stateless after construction; we
 * reuse the module-level instances.
 */
const AIR_PLANES: ReadonlyArray<{ plane: Plane; y: number }> = (() => {
  const list: { plane: Plane; y: number }[] = [];
  for (let y = 0; y <= MAX_AIR_Y; y += 1) {
    list.push({ plane: new Plane(new Vector3(0, 1, 0), -y), y });
  }
  return list;
})();

/**
 * Resolve a pointer position (in NDC) to a snapped voxel coord,
 * walking the four horizontal planes y=0..y=3.
 *
 * @param raycaster  The Three.Raycaster instance owned by the caller
 *                   (kept as a parameter so this function does not
 *                   allocate one per call, and so tests can inject a
 *                   deterministic mock).
 * @param camera     The camera the raycaster projects from.
 * @param ndcX       Normalised device X in [-1, +1].
 * @param ndcY       Normalised device Y in [-1, +1].
 * @returns          Snapped voxel coord, or `null` on complete miss.
 */
export function pickAirCoord(
  raycaster: Raycaster,
  camera: Camera,
  ndcX: number,
  ndcY: number,
): VoxelCoord | null {
  raycaster.setFromCamera(new Vector2(ndcX, ndcY), camera);

  // Walk every plane; collect the lowest-y hit. We iterate in plane
  // order (y=0,1,2,3), and the first hit we see on a strictly lower-y
  // plane replaces any higher-y hit, so we naturally end up with the
  // minimum-y hit at the end without a second sort pass.
  const target = new Vector3();
  let bestPoint: Vector3 | null = null;
  let bestY = Infinity;

  for (const { plane, y } of AIR_PLANES) {
    const hit = raycaster.ray.intersectPlane(plane, target);
    if (hit && y < bestY) {
      // Copy the point out of the reused `target` Vector3 — Three's
      // intersectPlane writes into the target, and the next iteration
      // would clobber it. Cloning via new Vector3 keeps the result
      // stable.
      bestPoint = new Vector3(target.x, target.y, target.z);
      bestY = y;
    }
  }

  if (bestPoint === null) {
    return null;
  }

  const snapped = snapToGrid([bestPoint.x, bestY, bestPoint.z]);
  return [snapped[0], Math.max(0, snapped[1]), snapped[2]];
}
